import React, { useState, useMemo } from 'react';
import { formatMontant } from '../utils/helpers';
import { computeLigne, RECETTES_LIGNES, DEPENSES_LIGNES, HIST_DEFAULT, HIST_YEARS } from './compta/comptaUtils';
import OngletImport   from './compta/OngletImport';
import OngletDefauts  from './compta/OngletDefauts';
import OngletJournal  from './compta/OngletJournal';
import OngletBilan           from './compta/OngletBilan';
import OngletCompteResultat  from './compta/OngletCompteResultat';

const TABS = [
  { id: 'import',   label: '📄 Import relevé',          desc: 'Import mensuel BFC & classification' },
  { id: 'defauts',  label: '❌ Défauts de paiement',    desc: 'Impayés, actions, alertes' },
  { id: 'journal',  label: '📒 Journal mensuel',         desc: 'Opérations, saisie, réconciliation' },
  { id: 'bilan',    label: '📊 Bilan annuel',            desc: 'Synthèse, graphiques, rapport AG' },
  { id: 'compte-resultat', label: '📋 Compte de résultat', desc: 'Compte de résultat simplifié 2021-présent' },
];

export default function ComptabiliteFinancier(props) {
  const {
    adherents, etablissements, cotisations, setCotisation, selectedYear,
    currentUsername, addJournal,
    comptabilite = {},
    saveComptaOperation, deleteComptaOperation, setComptaMoisData, clearComptaMoisOperations,
    compteResultat, saveCompteResultat, saveAdherent,
  } = props;

  const [activeTab,  setActiveTab]  = useState('import');
  const [activeMois, setActiveMois] = useState(new Date().getMonth() + 1);
  const [mouvModal,  setMouvModal]  = useState(false);

  const sharedTabProps = {
    adherents, etablissements, cotisations, setCotisation, selectedYear,
    currentUsername, addJournal,
    comptabilite,
    saveComptaOperation, deleteComptaOperation, setComptaMoisData, clearComptaMoisOperations,
    activeMois, setActiveMois, saveAdherent,
  };

  // ── KPI global (persistant toutes sections) ──
  const globalKPI = useMemo(() => {
    const yearData = comptabilite[selectedYear] || {};
    let impayesActifs = 0, totalDefauts = 0, regularises = 0, moisImportes = 0;
    let lastSoldeFinal = null, lastSoldeMois = null;
    for (let m = 1; m <= 12; m++) {
      const md = yearData[m];
      if (md?.dateImport || (md?.operations?.length > 0)) moisImportes++;
      if (md?.soldeFinal != null) { lastSoldeFinal = md.soldeFinal; lastSoldeMois = m; }
      (md?.operations || []).forEach(o => {
        if (o.type === 'B') {
          totalDefauts++;
          if (o.statut === 'regularise') regularises++;
          else impayesActifs++;
        }
      });
    }
    const tauxRecouvrement = totalDefauts > 0 ? Math.round((regularises / totalDefauts) * 100) : 100;
    // Résultat structuré = même calcul que Compte de Résultat (historique + override manuel)
    const hist = compteResultat?.historique || {};
    const isHistYear = HIST_YEARS.includes(selectedYear);
    const lineVal = (ligne) => {
      const manual = hist[ligne.id]?.[selectedYear];
      if (manual !== undefined) return manual;
      if (isHistYear) {
        const def = HIST_DEFAULT[ligne.id]?.[selectedYear];
        if (def !== undefined) return def;
      }
      return computeLigne(ligne, comptabilite, selectedYear).total;
    };
    const totRec = RECETTES_LIGNES.reduce((s, l) => s + lineVal(l), 0);
    const totDep = DEPENSES_LIGNES.reduce((s, l) => s + lineVal(l), 0);
    const soldeCumul = Math.round((totRec - totDep) * 100) / 100;
    // Détail par mois pour le modal
    const MOIS_C = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'];
    const parMois = Array.from({ length: 12 }, (_, i) => {
      const m = i + 1;
      const ops = (yearData[m]?.operations || []);
      const encaissements = RECETTES_LIGNES.reduce((s, l) => {
        const mOps = ops.filter(o => (l.types?.includes(o.type) || l.categories?.includes(o.categorie)) && (o.credit || 0) > 0.005);
        return s + mOps.reduce((a, o) => a + (o.credit || 0), 0);
      }, 0);
      const depenses = DEPENSES_LIGNES.reduce((s, l) => {
        const mOps = ops.filter(o => (l.types?.includes(o.type) || l.categories?.includes(o.categorie)) && (o.debit || 0) > 0.005);
        return s + mOps.reduce((a, o) => a + (o.debit || 0), 0);
      }, 0);
      const imported = !!(yearData[m]?.dateImport || ops.length > 0);
      return { mois: MOIS_C[i], encaissements, depenses, net: Math.round((encaissements - depenses) * 100) / 100, imported };
    });
    return { soldeCumul, totRec, totDep, impayesActifs, tauxRecouvrement, moisImportes, lastSoldeFinal, lastSoldeMois, parMois };
  }, [comptabilite, selectedYear]);

  // ── Count défauts for badge ──
  const nbDefauts = Object.values(comptabilite[selectedYear] || {})
    .reduce((s, md) => s + (md.operations || []).filter(o => o.type === 'B' && o.statut !== 'regularise').length, 0);

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <div className="page-title">📒 Comptabilité & Suivi Financier</div>
          <div className="page-subtitle">Gestion financière syndicale — Exercice {selectedYear}</div>
        </div>
      </div>

      {/* KPI global persistant */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(170px,1fr))', gap:12, marginBottom:20 }}>
        <div className="stat-card" style={{ borderTopColor: globalKPI.soldeCumul >= 0 ? '#166534' : '#991b1b', cursor:'pointer' }}
          onClick={() => setMouvModal(true)}
          title="Cliquez pour le détail par mois">
          <div className="stat-label">Mouvement net {selectedYear}</div>
          <div className="stat-value" style={{ color: globalKPI.soldeCumul >= 0 ? '#166534' : '#991b1b', fontSize:20 }}>
            {formatMontant(globalKPI.soldeCumul)}
          </div>
          <div className="stat-sub" style={{ fontSize:10, color:'#9ca3af' }}>encaissements − dépenses → voir détail</div>
        </div>
        {globalKPI.lastSoldeFinal != null && (
          <div className="stat-card" style={{ borderTopColor: '#0369a1' }}>
            <div className="stat-label">Solde bancaire relevé</div>
            <div className="stat-value" style={{ color: '#0369a1', fontSize:20 }}>
              {formatMontant(globalKPI.lastSoldeFinal)}
            </div>
            <div className="stat-sub" style={{ fontSize:10, color:'#9ca3af' }}>au {['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'][globalKPI.lastSoldeMois - 1]} {selectedYear} (dernier relevé)</div>
          </div>
        )}
        <div className="stat-card" style={{ borderTopColor: globalKPI.impayesActifs > 0 ? '#991b1b' : '#166534', cursor: globalKPI.impayesActifs > 0 ? 'pointer' : 'default' }}
          onClick={() => globalKPI.impayesActifs > 0 && setActiveTab('defauts')}
          title={globalKPI.impayesActifs > 0 ? 'Cliquez pour voir le listing des impayés' : undefined}>
          <div className="stat-label">Impayés actifs</div>
          <div className="stat-value" style={{ color: globalKPI.impayesActifs > 0 ? '#991b1b' : '#166534', fontSize:20 }}>
            {globalKPI.impayesActifs}
          </div>
          <div className="stat-sub">non régularisés {globalKPI.impayesActifs > 0 && <span style={{ fontSize:10, color:'#991b1b' }}>→ voir listing</span>}</div>
        </div>
        <div className="stat-card" style={{ borderTopColor: globalKPI.tauxRecouvrement >= 80 ? '#166534' : '#d97706' }}>
          <div className="stat-label">Taux recouvrement</div>
          <div className="stat-value" style={{ color: globalKPI.tauxRecouvrement >= 80 ? '#166534' : '#d97706', fontSize:20 }}>
            {globalKPI.tauxRecouvrement}&nbsp;%
          </div>
        </div>
        <div className="stat-card" style={{ borderTopColor:'#1d4ed8' }}>
          <div className="stat-label">Mois importés</div>
          <div className="stat-value" style={{ color:'#1d4ed8', fontSize:20 }}>
            {globalKPI.moisImportes}<span style={{ fontSize:14, fontWeight:400 }}> / 12</span>
          </div>
          <div className="stat-sub">{12 - globalKPI.moisImportes} restant(s)</div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="tabs" style={{ marginBottom: 20 }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            style={{ position: 'relative' }}
          >
            {tab.label}
            {tab.id === 'defauts' && nbDefauts > 0 && (
              <span style={{
                position: 'absolute', top: -6, right: -6,
                background: '#CC0000', color: '#fff',
                borderRadius: '50%', width: 18, height: 18,
                fontSize: 10, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {nbDefauts}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Modal Mouvement Net détail */}
      {mouvModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={() => setMouvModal(false)}>
          <div style={{ background:'#fff', borderRadius:12, padding:24, maxWidth:560, width:'100%', maxHeight:'85vh', overflowY:'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
              <div>
                <div style={{ fontWeight:700, fontSize:16 }}>📊 Mouvement net {selectedYear}</div>
                <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>Encaissements − Dépenses par mois (relevés importés)</div>
              </div>
              <button style={{ background:'none', border:'none', fontSize:18, cursor:'pointer', color:'#6b7280' }} onClick={() => setMouvModal(false)}>✕</button>
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                  <th style={{ textAlign:'left',  padding:'8px 10px', color:'#6b7280', fontWeight:700 }}>Mois</th>
                  <th style={{ textAlign:'right', padding:'8px 10px', color:'#166534', fontWeight:700 }}>Encaissements</th>
                  <th style={{ textAlign:'right', padding:'8px 10px', color:'#991b1b', fontWeight:700 }}>Dépenses</th>
                  <th style={{ textAlign:'right', padding:'8px 10px', color:'#1d4ed8', fontWeight:700 }}>Net</th>
                </tr>
              </thead>
              <tbody>
                {globalKPI.parMois.map((row, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid #f3f4f6', opacity: row.imported ? 1 : 0.4 }}>
                    <td style={{ padding:'7px 10px', fontWeight:600 }}>
                      {row.mois}
                      {!row.imported && <span style={{ fontSize:10, color:'#9ca3af', marginLeft:6 }}>non importé</span>}
                    </td>
                    <td style={{ textAlign:'right', padding:'7px 10px', color:'#166534', fontWeight:600 }}>
                      {row.encaissements > 0 ? '+' + formatMontant(row.encaissements) : '—'}
                    </td>
                    <td style={{ textAlign:'right', padding:'7px 10px', color:'#991b1b', fontWeight:600 }}>
                      {row.depenses > 0 ? '−' + formatMontant(row.depenses) : '—'}
                    </td>
                    <td style={{ textAlign:'right', padding:'7px 10px', fontWeight:700,
                      color: row.net > 0 ? '#166534' : row.net < 0 ? '#991b1b' : '#9ca3af' }}>
                      {row.imported ? (row.net > 0 ? '+' : '') + formatMontant(row.net) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ borderTop:'2px solid #374151', background:'#f9fafb' }}>
                  <td style={{ padding:'9px 10px', fontWeight:800 }}>TOTAL</td>
                  <td style={{ textAlign:'right', padding:'9px 10px', color:'#166534', fontWeight:800 }}>
                    +{formatMontant(globalKPI.parMois.reduce((s,r) => s + r.encaissements, 0))}
                  </td>
                  <td style={{ textAlign:'right', padding:'9px 10px', color:'#991b1b', fontWeight:800 }}>
                    −{formatMontant(globalKPI.parMois.reduce((s,r) => s + r.depenses, 0))}
                  </td>
                  <td style={{ textAlign:'right', padding:'9px 10px', fontWeight:800,
                    color: globalKPI.soldeCumul >= 0 ? '#166534' : '#991b1b' }}>
                    {(globalKPI.soldeCumul >= 0 ? '+' : '') + formatMontant(globalKPI.soldeCumul)}
                  </td>
                </tr>
              </tfoot>
            </table>
            <div style={{ fontSize:11, color:'#9ca3af', marginTop:12 }}>Les mois non importés (opacité réduite) ne sont pas comptabilisés.</div>
          </div>
        </div>
      )}

      {/* Tab content */}
      {activeTab === 'import'          && <OngletImport        {...sharedTabProps} />}
      {activeTab === 'defauts'         && <OngletDefauts       {...sharedTabProps} />}
      {activeTab === 'journal'         && <OngletJournal       {...sharedTabProps} />}
      {activeTab === 'bilan'           && <OngletBilan         {...sharedTabProps} />}
      {activeTab === 'compte-resultat' && <OngletCompteResultat
        comptabilite={comptabilite}
        selectedYear={selectedYear}
        compteResultat={compteResultat}
        saveCompteResultat={saveCompteResultat}
      />}
    </div>
  );
}
